import { Fragment, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { media } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { Image } from '@/libraries/design/components/image/src/image';

import { JiraOnboardingContainer } from '../jira/jira-onboarding-container/jira-onboarding-container';
import { useIsStandardizedOnboardingCtas } from '../onboarding-wizard/hooks/experiment-hooks.ts';

export type HdyhauOptionValue =
	| 'social'
	| 'aiTools'
	| 'review'
	| 'atlassian'
	| 'friend'
	| 'workplace'
	| 'youtube'
	| 'event'
	| 'search'
	| 'other';

type HdyhauOption = {
	readonly value: HdyhauOptionValue;
	readonly label: string;
	readonly icon: ReactNode;
};

type HdyhauFollowUpOption = {
	readonly value: string;
	readonly label: string;
	readonly icon?: ReactNode;
};

type HdyhauFollowUpConfig = {
	readonly question?: string;
	readonly options?: ReadonlyArray<HdyhauFollowUpOption>;
	readonly input?: {
		readonly label: string;
		readonly placeholder: string;
	};
};

type HdyhauViewProps = {
	readonly onSelect?: (value: HdyhauOptionValue) => void;
	readonly onSkip?: () => void;
	readonly onNext?: (value: HdyhauOptionValue | null) => void;
};

const hdyhauAnalyticsSourceByOption: Record<HdyhauOptionValue, string> = {
	social: 'socialMedia',
	aiTools: 'aiTools',
	review: 'reviewSiteBlogPodcast',
	atlassian: 'atlassianSalesRepPartnerProduct',
	friend: 'friendOrColleague',
	workplace: 'workplaceOrEmployer',
	youtube: 'youtubeOrVideo',
	event: 'eventWebinarCommunity',
	search: 'googleSearchEngine',
	other: 'other',
};

export const HdyhauView = ({ onSelect, onSkip, onNext }: HdyhauViewProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isStandardizedCtas = useIsStandardizedOnboardingCtas();
	const [selectedOption, setSelectedOption] = useState<HdyhauOptionValue | null>(null);
	const [selectedFollowUpOption, setSelectedFollowUpOption] = useState<string | null>(null);
	const [otherDetails, setOtherDetails] = useState('');

	useEffect(() => {
		createAnalyticsEvent({
			action: 'viewed',
			eventType: 'screen',
			name: 'hdyhauScreen',
		}).fire();
	}, [createAnalyticsEvent]);

	const options = useMemo<ReadonlyArray<HdyhauOption>>(
		() => [
			{
				value: 'social',
				label: intl.formatMessage({
					id: 'ConfirmationPage.Hdyhau.socialMedia',
					defaultMessage: 'Social media (e.g LinkedIn, Reddit, etc)',
				}),
				icon: (
					<Image
						src={
							'https://dam-cdn.atl.orangelogic.com/AssetLink/863eve586is6088l0we53aqwm1602be7.svg'
						}
						alt=""
						css={iconImage}
					/>
				),
			},
			{
				value: 'aiTools',
				label: intl.formatMessage({
					id: 'ConfirmationPage.Hdyhau.aiTools',
					defaultMessage: 'AI tools (e.g. ChatGPT, Gemini, etc)',
				}),
				icon: (
					<Image
						src={
							'https://dam-cdn.atl.orangelogic.com/AssetLink/w6i1vklf20llcp17002xxj0go65hq8vg.svg'
						}
						alt=""
						css={iconImage}
					/>
				),
			},
			{
				value: 'review',
				label: intl.formatMessage({
					id: 'ConfirmationPage.Hdyhau.reviewSiteBlogPodcast',
					defaultMessage: 'Review site, blog, or podcast',
				}),
				icon: (
					<Image
						src={
							'https://dam-cdn.atl.orangelogic.com/AssetLink/xp0650e26yyqu368yhsld7iim70o3v60.svg'
						}
						alt=""
						css={iconImage}
					/>
				),
			},
			{
				value: 'atlassian',
				label: intl.formatMessage({
					id: 'ConfirmationPage.Hdyhau.atlassianSalesRepPartnerProduct',
					defaultMessage: 'Atlassian sales rep, partner, or product',
				}),
				icon: (
					<Image
						src={
							'https://dam-cdn.atl.orangelogic.com/AssetLink/cc8ou503qgcpea6v85iq6rl73314tvt5.svg'
						}
						alt=""
						css={iconImage}
					/>
				),
			},
			{
				value: 'friend',
				label: intl.formatMessage({
					id: 'ConfirmationPage.Hdyhau.friendOrColleague',
					defaultMessage: 'Friend or colleague',
				}),
				icon: (
					<Image
						src={
							'https://dam-cdn.atl.orangelogic.com/AssetLink/ycu8x0sk314kp6b6b7pmdk2u2a804au6.svg'
						}
						alt=""
						css={iconImage}
					/>
				),
			},
			{
				value: 'workplace',
				label: intl.formatMessage({
					id: 'ConfirmationPage.Hdyhau.workplaceOrEmployer',
					defaultMessage: 'Workplace or employer',
				}),
				icon: (
					<Image
						src={
							'https://dam-cdn.atl.orangelogic.com/AssetLink/4074626yp65f306w651x66amh4tlf1de.svg'
						}
						alt=""
						css={iconImage}
					/>
				),
			},
			{
				value: 'youtube',
				label: intl.formatMessage({
					id: 'ConfirmationPage.Hdyhau.youtubeOrVideo',
					defaultMessage: 'Youtube or video',
				}),
				icon: (
					<Image
						src={
							'https://dam-cdn.atl.orangelogic.com/AssetLink/3syw06ejft55cmt7gfmww2luk4t6xbsi.svg'
						}
						alt=""
						css={iconImage}
					/>
				),
			},
			{
				value: 'event',
				label: intl.formatMessage({
					id: 'ConfirmationPage.Hdyhau.eventWebinarCommunity',
					defaultMessage: 'Event, webinar, or community',
				}),
				icon: (
					<Image
						src={
							'https://dam-cdn.atl.orangelogic.com/AssetLink/b3dg8r66i8pqtjt75456445l1775532i.svg'
						}
						alt=""
						css={iconImage}
					/>
				),
			},
			{
				value: 'search',
				label: intl.formatMessage({
					id: 'ConfirmationPage.Hdyhau.googleSearchEngine',
					defaultMessage: 'Google / search engine',
				}),
				icon: (
					<Image
						src={
							'https://dam-cdn.atl.orangelogic.com/AssetLink/528203a87a2r23ugg4364120fv68yt45.svg'
						}
						alt=""
						css={iconImage}
					/>
				),
			},
			{
				value: 'other',
				label: intl.formatMessage({
					id: 'ConfirmationPage.Hdyhau.other',
					defaultMessage: 'Other',
				}),
				icon: <ShowMoreHorizontalIcon label="" color={token('color.icon')} size="medium" />,
			},
		],
		[intl],
	);
	const optionRows = useMemo(() => createOptionRows(options), [options]);
	const followUpConfigByOption = useMemo<Partial<Record<HdyhauOptionValue, HdyhauFollowUpConfig>>>(
		() => ({
			social: {
				question: intl.formatMessage({
					id: 'ConfirmationPage.Hdyhau.socialFollowUpQuestion',
					defaultMessage: 'Which social media?',
				}),
				options: [
					{
						value: 'linkedin',
						label: intl.formatMessage({
							id: 'ConfirmationPage.Hdyhau.socialFollowUpLinkedIn',
							defaultMessage: 'LinkedIn',
						}),
						icon: (
							<Image
								src={
									'https://dam-cdn.atl.orangelogic.com/AssetLink/b1x5ur85h5k11ma17jqm50jyq130m7y4.svg'
								}
								alt=""
								css={followUpButtonIcon}
							/>
						),
					},
					{
						value: 'reddit',
						label: intl.formatMessage({
							id: 'ConfirmationPage.Hdyhau.socialFollowUpReddit',
							defaultMessage: 'Reddit',
						}),
						icon: (
							<Image
								src={
									'https://dam-cdn.atl.orangelogic.com/AssetLink/8261o52s81uw0nk248b3altsdx4877jl.png'
								}
								alt=""
								css={followUpButtonIcon}
							/>
						),
					},
					{
						value: 'xTwitter',
						label: intl.formatMessage({
							id: 'ConfirmationPage.Hdyhau.socialFollowUpXTwitter',
							defaultMessage: 'X / Twitter',
						}),
						icon: (
							<Image
								src={
									'https://dam-cdn.atl.orangelogic.com/AssetLink/04233c1h1oe2c35h2kb308hkrc1t6o25.svg'
								}
								alt=""
								css={followUpButtonIcon}
							/>
						),
					},
					{
						value: 'tiktok',
						label: intl.formatMessage({
							id: 'ConfirmationPage.Hdyhau.socialFollowUpTikTok',
							defaultMessage: 'TikTok',
						}),
						icon: (
							<Image
								src={
									'https://dam-cdn.atl.orangelogic.com/AssetLink/x6l41d3njyc2jr6hlcui3ex3gt60g5e2.svg'
								}
								alt=""
								css={followUpButtonIcon}
							/>
						),
					},
					{
						value: 'instagram',
						label: intl.formatMessage({
							id: 'ConfirmationPage.Hdyhau.socialFollowUpInstagram',
							defaultMessage: 'Instagram',
						}),
						icon: (
							<Image
								src={
									'https://dam-cdn.atl.orangelogic.com/AssetLink/54y2unx7bqf1gnl0w24p84816vmee0ph.svg'
								}
								alt=""
								css={followUpButtonIcon}
							/>
						),
					},
					{
						value: 'other',
						label: intl.formatMessage({
							id: 'ConfirmationPage.Hdyhau.socialFollowUpOther',
							defaultMessage: 'Other',
						}),
						icon: <ShowMoreHorizontalIcon label="" color={token('color.icon')} size="small" />,
					},
				],
			},
			aiTools: {
				question: intl.formatMessage({
					id: 'ConfirmationPage.Hdyhau.aiToolsFollowUpQuestion',
					defaultMessage: 'Which AI tool?',
				}),
				options: [
					{
						value: 'chatgpt',
						label: intl.formatMessage({
							id: 'ConfirmationPage.Hdyhau.aiToolsFollowUpChatGpt',
							defaultMessage: 'ChatGPT',
						}),
						icon: (
							<Image
								src={
									'https://dam-cdn.atl.orangelogic.com/AssetLink/533q4113b2mqu22re24yq56pysu7g6xe.svg'
								}
								alt=""
								css={followUpButtonIcon}
							/>
						),
					},
					{
						value: 'claude',
						label: intl.formatMessage({
							id: 'ConfirmationPage.Hdyhau.aiToolsFollowUpClaude',
							defaultMessage: 'Claude',
						}),
						icon: (
							<Image
								src={
									'https://dam-cdn.atl.orangelogic.com/AssetLink/ed16d6kj6m63qax3825073t75mjog8nf.svg'
								}
								alt=""
								css={followUpButtonIcon}
							/>
						),
					},
					{
						value: 'copilot',
						label: intl.formatMessage({
							id: 'ConfirmationPage.Hdyhau.aiToolsFollowUpCopilot',
							defaultMessage: 'Copilot',
						}),
						icon: (
							<Image
								src={
									'https://dam-cdn.atl.orangelogic.com/AssetLink/j0p2k75ujnasj16mudr4hq8ep5c4w651.svg'
								}
								alt=""
								css={followUpButtonIcon}
							/>
						),
					},
					{
						value: 'gemini',
						label: intl.formatMessage({
							id: 'ConfirmationPage.Hdyhau.aiToolsFollowUpGemini',
							defaultMessage: 'Gemini',
						}),
						icon: (
							<Image
								src={
									'https://dam-cdn.atl.orangelogic.com/AssetLink/47wr5442ryp3cq8vtd715b1810811f5f.svg'
								}
								alt=""
								css={followUpButtonIcon}
							/>
						),
					},
					{
						value: 'perplexity',
						label: intl.formatMessage({
							id: 'ConfirmationPage.Hdyhau.aiToolsFollowUpPerplexity',
							defaultMessage: 'Perplexity',
						}),
						icon: (
							<Image
								src={
									'https://dam-cdn.atl.orangelogic.com/AssetLink/0u1d14k3nl1c417eu5850265we5ol4pn.svg'
								}
								alt=""
								css={followUpButtonIcon}
							/>
						),
					},
					{
						value: 'other',
						label: intl.formatMessage({
							id: 'ConfirmationPage.Hdyhau.aiToolsFollowUpOther',
							defaultMessage: 'Other',
						}),
						icon: <ShowMoreHorizontalIcon label="" color={token('color.icon')} size="small" />,
					},
				],
			},
			other: {
				input: {
					label: intl.formatMessage({
						id: 'ConfirmationPage.Hdyhau.otherDetailsInputLabel',
						defaultMessage: 'Tell us more about how you heard about Jira',
					}),
					placeholder: intl.formatMessage({
						id: 'ConfirmationPage.Hdyhau.otherDetailsInputPlaceholder',
						defaultMessage: 'Tell us more...',
					}),
				},
			},
		}),
		[intl],
	);
	const selectedFollowUpConfig = selectedOption
		? followUpConfigByOption[selectedOption]
		: undefined;

	const header = intl.formatMessage({
		id: 'ConfirmationPage.Hdyhau.header',
		defaultMessage: 'How did you hear about Jira?',
	});

	const handleSelect = useCallback(
		(value: HdyhauOptionValue) => {
			setSelectedOption(value);
			setSelectedFollowUpOption(null);
			setOtherDetails('');
			onSelect?.(value);
		},
		[onSelect],
	);

	const handleFollowUpSelect = useCallback((value: string) => {
		setSelectedFollowUpOption(value);
	}, []);

	const handleOtherDetailsChange = useCallback((value: string) => {
		setOtherDetails(value);
	}, []);

	const handleSkip = useCallback(() => {
		createAnalyticsEvent({
			eventType: 'ui',
			actionSubject: 'form',
			action: 'skipped',
			actionSubjectId: 'hdyhauForm',
		}).fire();
		onSkip?.();
	}, [createAnalyticsEvent, onSkip]);

	const handleNext = useCallback(() => {
		if (!selectedOption) {
			handleSkip();
			return;
		}

		const freeText = otherDetails.trim();

		createAnalyticsEvent({
			eventType: 'ui',
			actionSubject: 'form',
			action: 'submitted',
			actionSubjectId: 'hdyhauForm',
			attributes: {
				hdyhauSource: hdyhauAnalyticsSourceByOption[selectedOption],
				hdyhauSubSource: selectedFollowUpOption,
				hdyhauFreeText: selectedOption === 'other' && freeText ? freeText : null,
			},
		}).fire();
		onNext?.(selectedOption);
	}, [
		createAnalyticsEvent,
		handleSkip,
		onNext,
		otherDetails,
		selectedFollowUpOption,
		selectedOption,
	]);

	const content = (
		<div css={stepContent}>
			<div css={optionsGrid}>
				{optionRows.map((row) => {
					return (
						<div key={row.map((option) => option.value).join('-')} css={optionRow}>
							{row.map((option) => (
								<Fragment key={option.value}>
									<HdyhauOptionCard
										option={option}
										hasSelection={selectedOption !== null}
										isSelected={selectedOption === option.value}
										onSelect={handleSelect}
									/>
									{selectedOption === option.value && selectedFollowUpConfig && (
										<HdyhauFollowUpPanel
											config={selectedFollowUpConfig}
											otherDetails={otherDetails}
											selectedValue={selectedFollowUpOption}
											onInputChange={handleOtherDetailsChange}
											onSelect={handleFollowUpSelect}
										/>
									)}
								</Fragment>
							))}
						</div>
					);
				})}
			</div>
			<div css={footerActions}>
				<div css={buttonCss}>
					<Button onClick={handleSkip}>
						{intl.formatMessage({
							id: 'ConfirmationPage.Hdyhau.skip',
							defaultMessage: 'Skip',
						})}
					</Button>
				</div>
				<div css={buttonCss}>
					<Button appearance="primary" onClick={handleNext}>
						{isStandardizedCtas
							? intl.formatMessage({
									id: 'ConfirmationPage.Hdyhau.continue',
									defaultMessage: 'Continue',
								})
							: intl.formatMessage({
									id: 'ConfirmationPage.Hdyhau.next',
									defaultMessage: 'Next',
								})}
					</Button>
				</div>
			</div>
		</div>
	);

	return (
		<JiraOnboardingContainer header={header} subheader="">
			{content}
		</JiraOnboardingContainer>
	);
};

const createOptionRows = (options: ReadonlyArray<HdyhauOption>) => {
	const rows: HdyhauOption[][] = [];

	for (let index = 0; index < options.length; index += 2) {
		rows.push(options.slice(index, index + 2));
	}

	return rows;
};

const HdyhauFollowUpPanel = ({
	config,
	otherDetails,
	selectedValue,
	onInputChange,
	onSelect,
}: {
	readonly config: HdyhauFollowUpConfig;
	readonly otherDetails: string;
	readonly selectedValue: string | null;
	readonly onInputChange: (value: string) => void;
	readonly onSelect: (value: string) => void;
}) => {
	return (
		<div css={followUpPanel}>
			{config.question && <p css={followUpQuestion}>{config.question}</p>}
			{config.options && (
				<div css={followUpOptions}>
					{config.options.map((option) => {
						const isSelected = selectedValue === option.value;

						return (
							<button
								key={option.value}
								type="button"
								aria-pressed={isSelected}
								css={[followUpButton, isSelected && selectedFollowUpButton]}
								onClick={() => onSelect(option.value)}
							>
								{option.icon && <span css={followUpButtonIconWrapper}>{option.icon}</span>}
								{option.label}
							</button>
						);
					})}
				</div>
			)}
			{config.input && (
				<Textfield
					aria-label={config.input.label}
					value={otherDetails}
					placeholder={config.input.placeholder}
					css={followUpInput}
					onChange={(event) => onInputChange(event.currentTarget.value)}
				/>
			)}
		</div>
	);
};

const HdyhauOptionCard = ({
	option,
	hasSelection,
	isSelected,
	onSelect,
}: {
	readonly option: HdyhauOption;
	readonly hasSelection: boolean;
	readonly isSelected: boolean;
	readonly onSelect: (value: HdyhauOptionValue) => void;
}) => {
	const [showArrow, setShowArrow] = useState(false);

	return (
		<button
			type="button"
			aria-pressed={isSelected}
			onBlur={() => setShowArrow(false)}
			onFocus={() => setShowArrow(true)}
			onMouseEnter={() => setShowArrow(true)}
			onMouseLeave={() => setShowArrow(false)}
			css={[
				optionButton,
				hasSelection && !isSelected && fadedOptionButton,
				isSelected && selectedOptionButton,
			]}
			onClick={() => onSelect(option.value)}
		>
			<span css={iconWrapper}>{option.icon}</span>
			<span css={optionLabel}>{option.label}</span>
			<span css={[optionArrowIconWrapper, showArrow && visibleOptionArrowIconWrapper]}>
				<ArrowRightIcon label="" />
			</span>
		</button>
	);
};

const stepContent = css({
	alignItems: 'center',
	display: 'flex',
	flexDirection: 'column',
	marginBlockStart: token('space.400'),
	width: '100%',
});

const optionsGrid = css({
	boxSizing: 'border-box',
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300'),
	maxWidth: '918px',
	width: '100%',
});

const optionRow = css({
	boxSizing: 'border-box',
	display: 'grid',
	gap: token('space.300'),
	gridTemplateColumns: '1fr',
	justifyItems: 'center',
	width: '100%',
	[media.above.sm]: {
		gridTemplateColumns: '1fr 1fr',
		justifyItems: 'stretch',
	},
});

const optionButton = css({
	alignItems: 'center',
	backgroundColor: token('elevation.surface'),
	border: `1px solid ${token('color.border')}`,
	borderRadius: token('radius.tile', '6px'),
	boxSizing: 'border-box',
	cursor: 'pointer',
	display: 'flex',
	flexDirection: 'row',
	gap: token('space.100'),
	height: '66px',
	justifyContent: 'flex-start',
	maxWidth: '447px',
	minWidth: 0,
	paddingBlock: token('space.100'),
	paddingInline: token('space.150'),
	position: 'relative',
	transition: 'border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease',
	width: '100%',
	':hover': {
		borderColor: token('color.border.brand'),
		boxShadow: `0 0 0 1px ${token('color.border.brand')}`,
	},
	':focus-visible': {
		outline: `2px solid ${token('color.border.focused')}`,
		outlineOffset: token('space.050'),
	},
	[media.above.sm]: {
		maxWidth: 'none',
	},
});

const fadedOptionButton = css({
	opacity: 0.4,
});

const selectedOptionButton = css({
	borderColor: token('color.border.brand'),
	boxShadow: `0 0 0 1px ${token('color.border.brand')}`,
});

const followUpPanel = css({
	backgroundColor: token('color.background.accent.blue.subtlest'),
	borderRadius: token('radius.tile', '6px'),
	boxSizing: 'border-box',
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.150'),
	gridColumn: '1 / -1',
	paddingBlock: token('space.200'),
	paddingInline: token('space.200'),
	width: '100%',
	[media.above.sm]: {
		order: 1,
	},
});

const followUpQuestion = css({
	color: token('color.text'),
	fontFamily: 'var(--atlassian-sans-font-family)',
	fontSize: '14px',
	fontWeight: 700,
	lineHeight: '20px',
	margin: 0,
});

const followUpOptions = css({
	display: 'flex',
	flexWrap: 'wrap',
	gap: token('space.100'),
});

const followUpButton = css({
	alignItems: 'center',
	backgroundColor: token('elevation.surface'),
	border: `1px solid ${token('color.border')}`,
	borderRadius: token('radius.small', '3px'),
	boxSizing: 'border-box',
	color: token('color.text'),
	cursor: 'pointer',
	display: 'inline-flex',
	fontFamily: 'var(--atlassian-sans-font-family)',
	fontSize: '14px',
	fontWeight: token('font.weight.regular'),
	gap: token('space.050'),
	lineHeight: '20px',
	minHeight: '24px',
	paddingBlock: token('space.025'),
	paddingInline: token('space.100'),
	':hover': {
		borderColor: token('color.border.brand'),
	},
	':focus-visible': {
		outline: `2px solid ${token('color.border.focused')}`,
		outlineOffset: token('space.050'),
	},
	[media.above.sm]: {
		fontSize: 'clamp(12px, 1.3vw, 14px)',
		lineHeight: '1.4',
	},
});

const selectedFollowUpButton = css({
	borderColor: token('color.border.brand'),
	boxShadow: `0 0 0 1px ${token('color.border.brand')}`,
	color: token('color.text.selected'),
});

const followUpButtonIconWrapper = css({
	alignItems: 'center',
	display: 'inline-flex',
	flexShrink: 0,
	height: '16px',
	justifyContent: 'center',
	width: '16px',
});

const followUpButtonIcon = css({
	display: 'block',
	height: '16px',
	objectFit: 'contain',
	width: '16px',
});

const followUpInput = css({
	width: '100%',
});

const iconWrapper = css({
	alignItems: 'center',
	display: 'flex',
	flexShrink: 0,
	height: '50px',
	justifyContent: 'center',
	width: '50px',
});

const iconImage = css({
	display: 'block',
	maxHeight: '36px',
	maxWidth: '36px',
});

const optionLabel = css({
	color: token('color.text'),
	flex: '1 1 auto',
	fontFamily: 'var(--atlassian-sans-font-family)',
	fontSize: '16px',
	fontWeight: 700,
	lineHeight: '20px',
	minWidth: 0,
	paddingInline: token('space.150'),
	textAlign: 'left',
	[media.above.sm]: {
		fontSize: 'clamp(12px, 1.5vw, 16px)',
	},
});

const optionArrowIconWrapper = css({
	alignItems: 'center',
	color: token('color.icon'),
	display: 'flex',
	flexShrink: 0,
	height: token('space.300'),
	justifyContent: 'center',
	marginInlineStart: 'auto',
	opacity: 0,
	pointerEvents: 'none',
	transition: 'opacity 0.15s ease',
	width: token('space.300'),
});

const visibleOptionArrowIconWrapper = css({
	opacity: 1,
});

const footerActions = css({
	alignItems: 'center',
	display: 'flex',
	flexDirection: 'row',
	gap: token('space.100'),
	justifyContent: 'center',
	marginBlockStart: token('space.400'),
	width: '100%',
});

const buttonCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		borderRadius: `${token('radius.small', '6px')} !important`,
		margin: '0 !important',
		paddingBlock: `${token('space.075')} !important`,
		paddingInline: `${token('space.150')} !important`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		fontFamily: 'var(--atlassian-sans-font-family) !important',
		fontSize: '14px !important',
		fontWeight: `${token('font.weight.medium')} !important`,
		lineHeight: `${token('space.300')} !important`,
		margin: '0 !important',
		textAlign: 'center',
	},
});
